import React, { useState, useEffect } from 'react'
import { Calendar, Tag, ArrowRight, BookOpen, Film, User, ChevronLeft, ChevronRight } from 'lucide-react'
import BlogPost from '../components/BlogPost'
import SocialShare from '../components/SocialShare'

const News = () => {
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [selectedAuthor, setSelectedAuthor] = useState('all')
  const [newsItems, setNewsItems] = useState([])
  const [selectedPost, setSelectedPost] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 6

  // ...existing code for categories, authors, sampleNews...

  const categories = [
    { id: 'all', name: 'All Posts' },
    { id: 'festival', name: 'Festival Selection' },
    { id: 'award', name: 'Awards' },
    { id: 'project', name: 'New Projects' },
    { id: 'production-update', name: 'Production Updates' },
    { id: 'filmmaking-insight', name: 'Filmmaking Insights' },
    { id: 'industry', name: 'Industry News' }
  ]

  const authors = [
    { id: 'all', name: 'All Authors' },
    { id: 'kamal-hossain', name: 'Kamal Hossain', role: 'Director & Founder' },
    { id: 'tanvir-ahmed', name: 'Tanvir Ahmed', role: 'Director of Photography' },
    { id: 'sadia-rahman', name: 'Sadia Rahman', role: 'Producer' }
  ]

  useEffect(() => {
    const savedNews = localStorage.getItem('shikdar_films_news')
    if (savedNews) {
      setNewsItems(JSON.parse(savedNews))
    }
    // Reset to page 1 when filters change
    setCurrentPage(1)
  }, [selectedCategory, selectedAuthor])

  const filteredNews = newsItems.filter(item => {
    const categoryMatch = selectedCategory === 'all' || item.category === selectedCategory
    const authorMatch = selectedAuthor === 'all' || item.author === selectedAuthor
    return categoryMatch && authorMatch
  })

  // Pagination logic
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentItems = filteredNews.slice(startIndex, endIndex)

  const goToPage = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    return new Date(dateString).toLocaleDateString('en-US', options)
  }

  const handleImageError = (e) => {
    e.target.src = 'https://images.pexels.com/photos/7991579/pexels-photo-7991579.jpeg?auto=compress&cs=tinysrgb&w=800'
  }

  const getCategoryIcon = (category) => {
    if (category === 'production-update' || category === 'filmmaking-insight') {
      return <BookOpen className="w-3 h-3" />
    }
    return <Film className="w-3 h-3" />
  }

  const getAuthorName = (authorId) => {
    const author = authors.find(a => a.id === authorId)
    return author ? author.name : 'Unknown Author'
  }

  if (selectedPost) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setSelectedPost(null)}
            className="inline-flex items-center gap-2 text-bengal-green hover:text-bengal-orange font-medium mb-8 transition-colors"
          >
            <ArrowRight className="w-5 h-5 rotate-180" />
            Back to All Posts
          </button>
          <BlogPost post={selectedPost} onPostSelect={setSelectedPost} />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="section-title text-4xl md:text-5xl text-gray-900 mb-4">
            News & Blog
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay updated with our latest projects, festival selections, production updates, and filmmaking insights
          </p>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Tag className="w-5 h-5 text-bengal-green" />
            <h3 className="font-semibold text-gray-900">Filter by Category</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-bengal-green text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-12">
          <div className="flex items-center gap-2 mb-3">
            <User className="w-5 h-5 text-bengal-orange" />
            <h3 className="font-semibold text-gray-900">Filter by Author</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {authors.map((author) => (
              <button
                key={author.id}
                onClick={() => setSelectedAuthor(author.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  selectedAuthor === author.id
                    ? 'bg-bengal-orange text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <div className="text-left">
                  <div>{author.name}</div>
                  {author.role && <div className="text-xs opacity-75">{author.role}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-gray-600">
          Showing {startIndex + 1}-{Math.min(endIndex, filteredNews.length)} of {filteredNews.length} {filteredNews.length === 1 ? 'post' : 'posts'}
        </div>

        {/* News/Blog Grid */}
        {currentItems.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {currentItems.map((item) => (
                <article 
                  key={item.id}
                  onClick={() => item.type === 'blog' && setSelectedPost(item)}
                  className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group ${
                    item.type === 'blog' ? 'cursor-pointer' : ''
                  }`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={item.image}
                      alt={item.title}
                      onError={handleImageError}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {item.type === 'blog' && (
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-bengal-orange text-white rounded-full text-xs font-semibold">
                          <BookOpen className="w-3 h-3" />
                          Blog Post
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-bengal-green/10 text-bengal-green rounded-full text-xs font-semibold capitalize">
                        {getCategoryIcon(item.category)}
                        {item.category.replace('-', ' ')}
                      </span>
                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span className="text-xs">{formatDate(item.date)}</span>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-gray-900 mb-3 section-title line-clamp-2">
                      {item.title}
                    </h3>

                    <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                      {item.excerpt}
                    </p>

                    <div className="flex items-center gap-2 mb-4 pb-4 border-b border-gray-200">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-600">By {getAuthorName(item.author)}</span>
                    </div>

                    <div className="flex items-center justify-between">
                      {item.type === 'blog' ? (
                        <button className="inline-flex items-center gap-2 text-bengal-green hover:text-bengal-orange font-medium text-sm transition-colors duration-200">
                          Read Article
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <span className="text-sm text-gray-500">News Update</span>
                      )}
                      {item.readTime && (
                        <span className="text-xs text-gray-500">{item.readTime}</span>
                      )}
                    </div>

                    {/* Social Share Buttons */}
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <SocialShare 
                        url={window.location.href}
                        title={item.title}
                        description={item.excerpt}
                      />
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  Previous
                </button>

                <div className="flex gap-2">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1
                    // Show first page, last page, current page, and pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          onClick={() => goToPage(page)}
                          className={`w-10 h-10 rounded-lg font-medium transition-all duration-200 ${
                            currentPage === page
                              ? 'bg-bengal-green text-white shadow-md'
                              : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return <span key={page} className="w-10 h-10 flex items-center justify-center text-gray-400">...</span>
                    }
                    return null
                  })}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg mb-2">No posts found</p>
            <p className="text-gray-400 text-sm">Try adjusting your filters</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default News